import { useState, useMemo } from 'react';
import { format } from 'date-fns';
import {
    Search,
    Plus,
    MoreHorizontal,
    ChevronLeft,
    ChevronRight,
    LoaderCircle,
    X
} from 'lucide-react';
import type { z } from 'zod';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/card';
import { Input } from '../../components/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/select';
import { Button } from '../../components/button';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/table';
import { Avatar, AvatarFallback, AvatarImage } from '../../components/avatar';
import { Badge } from '../../components/badge';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/dropdown-menu';
import Drawer from '../Drawer';
import { AddStaffForm, AddStaffFormSchema } from '../forms/AddStaff';
import { countries } from '../../lib/CountryCodes';

const ITEMS_PER_PAGE = 5;

export type StaffRecord = {
    id: string;
    full_name: string;
    email: string;
    profile_pic: string;
    is_active: boolean;
    job_title: string;
    phone_number: string;
    created_at: string;
};

export type StaffTableProps = {
    icon: string;
    staffs: StaffRecord[] | undefined;
    isLoading: boolean;
    /** Gates the Add/Edit/Deactivate affordances — read-only staff still see the list. */
    canManage: boolean;
    onView: (staffId: string) => void;
    onDeactivate: (staffId: string) => void;
    onActivate: (staffId: string) => void;
    addStaffOpen: boolean;
    onAddStaffOpenChange: (open: boolean) => void;
    selectedStaff: StaffRecord | null;
    onSelectStaff: (staff: StaffRecord | null) => void;
    onCreateStaff: (values: z.infer<typeof AddStaffFormSchema>) => void;
    handleSendToUpload: (files: File[]) => Promise<string[]>;
    mediaLoading: boolean;
    isSubmitError: boolean;
    isSubmitting: boolean;
    submitError?: Error | null;
};

export default function StaffTable({
    icon,
    staffs,
    isLoading,
    canManage,
    onView,
    onDeactivate,
    onActivate,
    addStaffOpen,
    onAddStaffOpenChange,
    selectedStaff,
    onSelectStaff,
    onCreateStaff,
    handleSendToUpload,
    mediaLoading,
    isSubmitError,
    isSubmitting,
    submitError
}: StaffTableProps) {
    const [searchTerm, setSearchTerm] = useState('');
    const [sortBy, setSortBy] = useState('name');
    const [currentPage, setCurrentPage] = useState(1);

    const handleStaffAction = (action: string, staffId: string) => {
        const staff = staffs?.find(s => s.id === staffId);
        if (!staff) return;

        if (action === 'edit') {
            onSelectStaff(staff);
            onAddStaffOpenChange(true);
        } else if (action === 'view') {
            onView(staffId);
        } else if (action === 'deactivate') {
            onDeactivate(staffId);
        } else if (action === 'activate') {
            onActivate(staffId);
        }
    };

    const filteredAndSortedData = useMemo(() => {
        const filtered = staffs?.filter(staff =>
            staff.full_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            staff.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
            staff.job_title.toLowerCase().includes(searchTerm.toLowerCase())
        );

        filtered?.sort((a, b) => {
            switch (sortBy) {
                case 'name':
                    return a.full_name.localeCompare(b.full_name);
                case 'email':
                    return a.email.localeCompare(b.email);
                case 'status':
                    return Number(b.is_active) - Number(a.is_active);
                case 'jobTitle':
                    return a.job_title.localeCompare(b.job_title);
                case 'lastLogin':
                    return new Date(a.created_at).getTime() - new Date(b.created_at).getTime();
                default:
                    return 0;
            }
        });

        return filtered;
    }, [searchTerm, sortBy, staffs]);

    const totalPages = filteredAndSortedData?.length ? Math.ceil(filteredAndSortedData.length / ITEMS_PER_PAGE) : 0;
    const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
    const endIndex = startIndex + ITEMS_PER_PAGE;
    const currentData = filteredAndSortedData?.slice(startIndex, endIndex) ?? [];

    const matchedCountry = countries.find((c) =>
        selectedStaff?.phone_number.startsWith(c.dial_code)
    );

    const existedValues = selectedStaff ? {
        staffName: selectedStaff.full_name,
        emailID: selectedStaff.email,
        phoneNumberCode: matchedCountry?.dial_code ?? '',
        phoneNumber: matchedCountry
            ? selectedStaff.phone_number.replace(matchedCountry.dial_code, '')
            : selectedStaff.phone_number,
        jobTitle: selectedStaff.job_title,
        profilePic: selectedStaff.profile_pic,
    } : undefined;

    return (
        <div className="w-full p-3 md:p-[24px]">
            <Card>
                <CardHeader className="pb-4">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-3">
                        <CardTitle className="text-2xl font-semibold">My Staff</CardTitle>
                        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
                            <div className="relative">
                                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
                                <Input
                                    placeholder="Search..."
                                    value={searchTerm}
                                    onChange={(e) => { setSearchTerm(e.target.value); setCurrentPage(1) }}
                                    className="pl-10 w-full sm:w-64"
                                />
                                {searchTerm !== '' && (
                                    <X
                                        className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4 cursor-pointer"
                                        onClick={() => { setSearchTerm(''); setCurrentPage(1) }}
                                    />
                                )}
                            </div>

                            <Select value={sortBy} onValueChange={setSortBy}>
                                <SelectTrigger className="w-full sm:w-32">
                                    <SelectValue placeholder="Sort by" />
                                </SelectTrigger>
                                <SelectContent>
                                    <SelectItem value="name">Name</SelectItem>
                                    <SelectItem value="email">Email</SelectItem>
                                    <SelectItem value="status">Status</SelectItem>
                                    <SelectItem value="jobTitle">Job Title</SelectItem>
                                    <SelectItem value="lastLogin">Created Date</SelectItem>
                                </SelectContent>
                            </Select>

                            {canManage && (
                                <Button onClick={() => onAddStaffOpenChange(true)} className="bg-black hover:bg-gray-800">
                                    <Plus className="w-4 h-4 mr-2" />
                                    Add Staff
                                </Button>
                            )}
                        </div>
                    </div>
                </CardHeader>

                <CardContent>
                    <div className="border rounded-lg overflow-x-auto">
                        <Table>
                            <TableHeader>
                                <TableRow className="bg-gray-50">
                                    <TableHead className="font-semibold"></TableHead>
                                    <TableHead className="font-semibold">Staff Email ID</TableHead>
                                    <TableHead className="font-semibold">Status</TableHead>
                                    <TableHead className="font-semibold">Job Title</TableHead>
                                    <TableHead className="font-semibold">Created Date</TableHead>
                                    <TableHead className="font-semibold w-12"></TableHead>
                                </TableRow>
                            </TableHeader>
                            <TableBody>
                                {isLoading ? (
                                    <TableRow>
                                        <TableCell colSpan={6} className="text-center py-8">
                                            <LoaderCircle className="animate-spin w-5 h-5 mx-auto text-gray-500" />
                                        </TableCell>
                                    </TableRow>
                                ) : currentData.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={6} className="text-center py-8 text-gray-500">
                                            No staff found.
                                        </TableCell>
                                    </TableRow>
                                ) : (
                                    currentData.map((staff) => (
                                        <TableRow key={staff.id} className="hover:bg-gray-100 cursor-pointer" onClick={() => onView(staff.id)}>
                                            <TableCell>
                                                <div className="flex items-center gap-3">
                                                    <Avatar className="w-8 h-8">
                                                        <AvatarImage src={staff.profile_pic} alt={staff.full_name} />
                                                        <AvatarFallback className="bg-gray-200 text-[#202020] text-sm">
                                                            {staff.full_name[0]}
                                                        </AvatarFallback>
                                                    </Avatar>
                                                    <p className="font-bold text-[#202020] text-[14px] capitalize">{staff.full_name}</p>
                                                </div>
                                            </TableCell>
                                            <TableCell>
                                                <p className="text-[14px] text-[#202020]">{staff.email}</p>
                                            </TableCell>
                                            <TableCell>
                                                <Badge className='text-[14px]' variant={staff.is_active ? 'activeStatus' : 'inactiveStatus'}>
                                                    {staff.is_active ? 'Active' : 'Inactive'}
                                                </Badge>
                                            </TableCell>
                                            <TableCell className="text-[#202020] text-[14px]">{staff.job_title}</TableCell>
                                            <TableCell className="text-[#202020] text-[14px]">
                                                {format(new Date(staff.created_at), 'dd MMMM yyyy')}
                                            </TableCell>

                                            {/* The row opens details on click. Clicks here (the menu button,
                                                and the menu items, whose events bubble through the portal in
                                                React) must not reach it. */}
                                            <TableCell onClick={(e) => e.stopPropagation()}>
                                                <DropdownMenu>
                                                    <DropdownMenuTrigger asChild className='cursor-pointer'>
                                                        <Button variant="ghost" className="h-8 w-8 p-0">
                                                            <MoreHorizontal className="h-4 w-4" />
                                                        </Button>
                                                    </DropdownMenuTrigger>
                                                    <DropdownMenuContent align="end">
                                                        <DropdownMenuItem
                                                            onClick={(e) => {
                                                                e.stopPropagation();
                                                                e.preventDefault();
                                                                handleStaffAction('view', staff.id);
                                                            }}
                                                        >
                                                            View Details
                                                        </DropdownMenuItem>
                                                        {canManage && (
                                                            <>
                                                                <DropdownMenuItem
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        e.preventDefault();
                                                                        handleStaffAction('edit', staff.id);
                                                                    }}
                                                                >
                                                                    Edit Staff
                                                                </DropdownMenuItem>
                                                                <DropdownMenuItem
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        e.preventDefault();
                                                                        handleStaffAction(staff.is_active ? 'deactivate' : 'activate', staff.id);
                                                                    }}
                                                                    className={staff.is_active ? "text-[#B00020]" : "text-[#005C00]"}
                                                                >
                                                                    {staff.is_active ? 'Deactivate' : 'Activate'}
                                                                </DropdownMenuItem>
                                                            </>
                                                        )}
                                                    </DropdownMenuContent>
                                                </DropdownMenu>
                                            </TableCell>
                                        </TableRow>
                                    ))
                                )}
                            </TableBody>
                        </Table>
                    </div>

                    <div className="flex items-center justify-between mt-4">
                        <div className="text-sm text-gray-500">
                            Page {currentPage} / {totalPages}
                        </div>
                        <div className="flex items-center gap-2">
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                                disabled={currentPage <= 1}
                            >
                                <ChevronLeft className="w-4 h-4 mr-1" />
                                Previous
                            </Button>
                            <Button
                                variant="outline"
                                size="sm"
                                onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                                disabled={currentPage >= totalPages || totalPages === 0}
                            >
                                Next
                                <ChevronRight className="w-4 h-4 ml-1" />
                            </Button>
                        </div>
                    </div>
                </CardContent>
            </Card>

            {canManage && (
                <Drawer
                    open={addStaffOpen}
                    setOpen={(val) => {
                        onAddStaffOpenChange(val);
                        if (!val) onSelectStaff(null);
                    }}
                    title={
                        <p className='flex items-center gap-[8px]'>
                            <img src={icon} alt="Create Order" />
                            {selectedStaff ? 'Update a staff' : 'Add a staff'}
                        </p>
                    }
                >
                    <AddStaffForm
                        onSubmit={onCreateStaff}
                        handleSendToUpload={handleSendToUpload}
                        mediaLoading={mediaLoading}
                        isError={isSubmitError}
                        isLoading={isSubmitting}
                        error={submitError}
                        existedValues={existedValues}
                    />
                </Drawer>
            )}
        </div>
    );
}
